<template>
    <div v-if="!accepted" class="cookie-banner" :class="{ 'minimized': isMinimized, 'dark-mode': isDarkMode }">
    <!-- Add minimize/expand button -->
    <div v-if="isMinimized" class="minimized-banner">
      <span @click="isMinimized = false" >{{ $t('cookies.consentcontinue') }}</span>
      <button @click="isMinimized = false" class="btn-minimize">
        <i class="fas fa-chevron-up"></i>
      </button>
    </div>
    <div class="cookie-content">
      <div class="banner-header">
        <h3 v-if="showDetails">{{ $t('cookies.preferences') }}</h3>
        <button @click="isMinimized = true" class="btn-minimize">
          <i class="fas fa-chevron-down"></i>
        </button>
        <div class="language-selector">
          <div class="language-dropdown" @click="isLanguageOpen = !isLanguageOpen">
            <span v-html="getFlag(getlocale())"></span>
            <span class="selected-language">{{ languageService.getLanguageName(getlocale(), true) }}</span>
            <i :class="['fas', isLanguageOpen ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
          </div>
          <div v-if="isLanguageOpen" class="language-options">
            <div 
              v-for="locale in availableLocales" 
              :key="locale" 
              class="language-option" 
              @click="setLocale(locale); isLanguageOpen = false"
            >
              <span v-html="getFlag(locale)"></span>
              <span>{{ languageService.getLanguageName(locale, true) }}</span>
            </div>
          </div>
        </div>

      </div>
      <!-- Main Banner when details are hidden -->
      <div v-if="!showDetails" class="cookie-main">
        <!-- Essential Consents -->
        <div>{{ $t('cookies.mainMessage') }}</div>
        <div class="consent-group required">
          <div class="consent-item">
            <input
              type="checkbox"
              id="termsAndConditions"
              v-model="consents.termsAndConditions"
              required
            />
            <label for="termsAndConditions">{{ $t('cookies.iacceptthe') }} <RouterLink to="/terms" class="link">{{ $t('cookies.termsAndConditions') }}</RouterLink></label>
          </div>

          <div class="consent-item">
            <input
              type="checkbox"
              id="privacyPolicy"
              v-model="consents.privacyPolicy"
              required
            />
            <label for="privacyPolicy">{{ $t('cookies.privacyagree') }} <RouterLink to="/privacy" class="link">{{ $t('cookies.privacyPolicy') }}</RouterLink></label>
          </div>
        </div>

        <div class="cookie-actions">
          <button 
            @click="showDetails = true" 
            class="btn-primary btn-ghost"
            :disabled="!canProceed"
            :title="!canProceed ? 'Please accept Terms and Privacy Policy first' : ''"
          >
            {{ $t('cookies.customize') }}
          </button>
          <button 
            @click="acceptAll" 
            class="btn-primary"
            :disabled="!canProceed"
            :title="!canProceed ? 'Please accept Terms and Privacy Policy first' : ''"
          >
            {{ $t('cookies.acceptAll') }}
          </button>
        </div>
      </div>
      
      <!-- Detailed Settings -->
      <div v-else class="cookie-details">
         <!-- GDPR Notice -->
         <div class="gdpr-notice">
          <i class="fas fa-info-circle"></i>
          <p>{{ $t('cookies.gdpr.notice') }}</p>
        </div>

        <!-- Data Handling with GDPR Information -->
        <div class="data-handling-section">
          <div class="data-handling-header" @click="toggleDataInfo">
            <div class="header-content">
              <i class="fas fa-shield-alt"></i>
              <h4>{{ $t('cookies.dataHandling.title') }}</h4>
            </div>
            <i :class="['fas', showDataInfo ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
          </div>

          <div v-if="showDataInfo" class="data-handling-details">
            <!-- Legal Basis (GDPR Requirement) -->
            <div class="info-group">
              <div class="info-icon">
                <i class="fas fa-balance-scale"></i>
              </div>
              <div class="info-content">
                <h5>{{ $t('cookies.gdpr.legalBasis.title') }}</h5>
                <ul>
                  <li>{{ $t('cookies.gdpr.legalBasis.consent') }}</li>
                  <li>{{ $t('cookies.gdpr.legalBasis.contract') }}</li>
                  <li>{{ $t('cookies.gdpr.legalBasis.legitimate') }}</li>
                </ul>
              </div>
            </div>

            <!-- Storage Location -->
            <div class="info-group">
              <div class="info-icon">
                <i class="fas fa-database"></i>
              </div>
              <div class="info-content">
                <h5>{{ $t('cookies.dataHandling.storage.title') }}</h5>
                <p>{{ $t('cookies.dataHandling.storage.description') }}</p>
                <div class="gdpr-transfer-notice">
                  <i class="fas fa-exclamation-triangle"></i>
                  <p>{{ $t('cookies.gdpr.transfer.notice') }}</p>
                </div>
              </div>
            </div>

            <!-- Protection Measures -->
            <div class="info-group">
              <div class="info-icon">
                <i class="fas fa-lock"></i>
              </div>
              <div class="info-content">
                <h5>{{ $t('cookies.dataHandling.protection.title') }}</h5>
                <ul>
                  <li>{{ $t('cookies.dataHandling.protection.item1') }}</li>
                  <li>{{ $t('cookies.dataHandling.protection.item2') }}</li>
                  <li>{{ $t('cookies.dataHandling.protection.item3') }}</li>
                  <li>{{ $t('cookies.dataHandling.protection.item4') }}</li>
                  <li>{{ $t('cookies.gdpr.protection.dpo') }}</li>
                </ul>
              </div>
            </div>

            <!-- GDPR Rights -->
            <div class="info-group">
              <div class="info-icon">
                <i class="fas fa-user-shield"></i>
              </div>
              <div class="info-content">
                <h5>{{ $t('cookies.gdpr.rights.title') }}</h5>
                <ul>
                  <li>{{ $t('cookies.gdpr.rights.access') }}</li>
                  <li>{{ $t('cookies.gdpr.rights.rectification') }}</li>
                  <li>{{ $t('cookies.gdpr.rights.erasure') }}</li>
                  <li>{{ $t('cookies.gdpr.rights.portability') }}</li>
                  <li>{{ $t('cookies.gdpr.rights.withdraw') }}</li>
                  <li>{{ $t('cookies.gdpr.rights.complaint') }}</li>
                </ul>
              </div>
            </div>

            <!-- Data Retention -->
            <div class="info-group">
              <div class="info-icon">
                <i class="fas fa-clock"></i>
              </div>
              <div class="info-content">
                <h5>{{ $t('cookies.gdpr.retention.title') }}</h5>
                <p>{{ $t('cookies.gdpr.retention.description') }}</p>
              </div>
            </div>

            <RouterLink :to="{ name: 'DataHandling' }" class="link data-handling-more">
              {{ $t('Full details: where your data is stored, who processes it and for how long') }}
              <i class="fas fa-arrow-right" aria-hidden="true"></i>
            </RouterLink>
          </div>
        </div>



        <!-- Essential Cookies - Always enabled -->
        <div class="consent-group">
          <div class="consent-header">
            <div>
              <h4>{{ $t('cookies.essential.title') }}</h4>
              <p>{{ $t('cookies.essential.description') }}</p>
            </div>
            <div class="consent-toggle">
              <input type="checkbox" checked disabled />
              <span class="required-label">{{ $t('cookies.required') }}</span>
            </div>
          </div>
        </div>

        <!-- Functional Cookies -->
        <div class="consent-group">
          <div class="consent-header">
            <div>
              <h4>{{ $t('cookies.functional.title') }}</h4>
              <p>{{ $t('cookies.functional.description') }}</p>
            </div>
            <div class="consent-toggle">
              <input 
                type="checkbox" 
                v-model="consents.functional"
              />
            </div>
          </div>
        </div>

        <!-- Analytics Cookies -->
        <div class="consent-group">
          <div class="consent-header">
            <div>
              <h4>{{ $t('cookies.analytics.title') }}</h4>
              <p>{{ $t('cookies.analytics.description') }}</p>
            </div>
            <div class="consent-toggle">
              <input 
                type="checkbox" 
                v-model="consents.analytics"
              />
            </div>
          </div>
        </div>

        <!-- Data Processing Consent -->
        <div class="consent-group required">
          <div class="consent-header">
            <div>
              <h4>{{ $t('cookies.dataProcessing.title') }}</h4>
              <p>{{ $t('cookies.dataProcessing.description') }}</p>
            </div>
            <div class="consent-toggle">
              <input 
                type="checkbox" 
                v-model="consents.dataProcessing"
                required
              />
              <span class="required-label">{{ $t('cookies.required') }}</span>
            </div>
          </div>
        </div>

        <!-- Data Transfer Consent -->
        <div class="consent-group required">
          <div class="consent-header">
            <div>
              <h4>{{ $t('cookies.dataTransfer.title') }}</h4>
              <p>{{ $t('cookies.dataTransfer.description') }}</p>
            </div>
            <div class="consent-toggle">
              <input 
                type="checkbox" 
                v-model="consents.dataTransfer"
                required
              />
              <span class="required-label">{{ $t('cookies.required') }}</span>
            </div>
          </div>
        </div>

        <!-- Marketing Consent -->
        <div class="consent-group">
          <div class="consent-header">
            <div>
              <h4>{{ $t('cookies.marketing.title') }}</h4>
              <p>{{ $t('cookies.marketing.description') }}</p>
            </div>
            <div class="consent-toggle">
              <input 
                type="checkbox" 
                v-model="consents.marketing"
              />
            </div>
          </div>
        </div>

        <div class="consent-actions">
          <button @click="savePreferences" class="btn-primary" :disabled="!canSave">
            {{ $t('cookies.savePreferences') }}
          </button>
        </div>

        <p class="consent-footer">
          {{ $t('cookies.footer') }}
          <RouterLink to="/privacy" class="link">{{ $t('cookies.privacyPolicy') }}</RouterLink>
          ·
          <RouterLink :to="{ name: 'DataHandling' }" class="link">{{ $t('Data Handling') }}</RouterLink>
        </p>
      </div>
    </div>
   
  </div>
</template>

<script setup>
import { ref, computed, onMounted  } from 'vue';
import { useSessionStore } from '@stores/session';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { getFlag, languageService } from '@services/languageService';
import { useThemeStore } from '@stores/themeStore';
import { storeToRefs } from 'pinia';
import { notify } from '@components/NotificationPlugin';
import { setupLocale } from '../i18nlocal.js';

const route = useRoute();
const router = useRouter();
const themeStore = useThemeStore();
const { isDarkMode } = storeToRefs(themeStore);
const { t, locale, availableLocales } = useI18n();

const isLanguageOpen = ref(false);

const getlocale = () => {
  return locale.value;
};

const setLocale = async (loc) => {
  // Pas 1.1: load the locale chunk and apply it (previously only the
  // persisted value changed and took effect on reload).
  await setupLocale(loc);
  useSessionStore().setLocale(loc).then(() => {
    if (route.path === '/login') {
      router.go(0);
    }
  });
};


const accepted = ref(true);
const showDetails = ref(false);
const showDataInfo = ref(true);
const isMinimized = ref(false);

const consents = ref({
  termsAndConditions: false,
  privacyPolicy: false,
  functional: false,
  analytics: false,
  dataProcessing: false,
  dataTransfer: false,
  marketing: false
});

// Add a ref for metadata
const consentMetadata = ref({
  timestamp: '',
  version: '',
  uploadedToServer: false
});

const toggleDataInfo = () => {
  showDataInfo.value = !showDataInfo.value;
};

// Computed property to check if user can proceed
const canProceed = computed(() => {
  return consents.value.termsAndConditions && consents.value.privacyPolicy;
});

const canSave = computed(() => {
  return consents.value.dataProcessing && consents.value.dataTransfer;
});


const acceptAll = () => {
  if (!canProceed.value) {
    notify('warning', t('Please accept Terms and Conditions and Privacy Policy to proceed'));
    return;
  }
  Object.keys(consents.value).forEach(key => {
    consents.value[key] = true;
  });
  saveConsents();
};


const savePreferences = () => {
  if (!canSave.value) {
    notify('warning', t('Please accept required consents'));
    return;
  }
  saveConsents();
};

const saveConsents = () => {
  // Save consents with metadata
  localStorage.setItem('userConsents', JSON.stringify({
    ...consents.value,
    ...consentMetadata.value,
    timestamp: new Date().toISOString(),
    version: '1.0',
    uploadedToServer: false
  }));
 
  // Update Google consent settings
  updateGoogleConsent();

  accepted.value = true;
};

const updateGoogleConsent = () => {
  if (window.gtag) {
    window.gtag('consent', 'update', {
      'analytics_storage': consents.value.analytics ? 'granted' : 'denied',
      'functionality_storage': consents.value.functional ? 'granted' : 'denied',
      'ad_storage': consents.value.marketing ? 'granted' : 'denied'
    });

    // Then manually send a pageview to capture the current page
    window.gtag('event', 'page_view', {
      page_title: document.title,
      page_location: window.location.href,
      page_path: window.location.pathname
    });
    
  }
};

// Check for existing consents
onMounted(() => {
  const savedConsents = localStorage.getItem('userConsents');
  let parsed = null;
  if (savedConsents) {
    try {
      parsed = JSON.parse(savedConsents);
    } catch {
      // A corrupted stored value must behave like no consent: show the banner.
      parsed = null;
    }
  }
  if (parsed) {
    // Separate consents from metadata
    const { timestamp, version, uploadedToServer, ...consentValues } = parsed;
    
    // Update consents
    consents.value = {
      ...consents.value,
      ...consentValues
    };

    // Update metadata
    consentMetadata.value = {
      timestamp,
      version,
      uploadedToServer
    };

  
    // Update Google Tag Manager consent based on saved preferences
    updateGoogleConsent();
    
    accepted.value = true;
  }
  else 
  {
    accepted.value = false;
  }
});
</script>

<style scoped>
/* ------------------------------------------------------------------
   Cookie consent — canonical tokens ( --cb-* aliases mapped to --adm-* )
   Dark mode via html.dark-mode (canonical) + .cookie-banner.dark-mode (compat).
------------------------------------------------------------------- */
.cookie-banner {
  --cb-panel: var(--adm-card, #ffffff);
  --cb-paper: var(--adm-bg, #f5f7fc);
  --cb-hover: var(--adm-card-head, #f2f4fb);
  --cb-line: var(--adm-border, #e4e8f5);
  --cb-ink: var(--adm-text-strong, #0a0f2c);
  --cb-body-text: var(--adm-text, #3a4160);
  --cb-muted: var(--adm-muted, #8a92b8);
  --cb-brand: var(--adm-accent, #003cff);
  --cb-brand-soft: var(--adm-accent-soft, #e6ecff);
  --cb-signal: var(--adm-success, #00e5a0);
  --cb-brand-glow: var(--adm-accent-soft, rgba(0, 60, 255, 0.25));
  --cb-display-font: var(--adm-font-display, 'Sora', sans-serif);
  --cb-body-font: var(--font-body, 'IBM Plex Sans', sans-serif);
  --cb-mono-font: var(--adm-font-mono, 'IBM Plex Mono', monospace);

  position: fixed;
  max-height: 45vh;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: var(--cb-panel);
  border-top: 1px solid var(--cb-line);
  box-shadow: 0 -16px 48px rgba(10, 15, 44, 0.14);
  z-index: 30; /* below the nav bars (z-40): never cover the mobile tab bar */
  font-family: var(--cb-body-font);
  color: var(--cb-body-text);
  transition: all 0.3s ease;
}

.cookie-banner.dark-mode,
html.dark-mode .cookie-banner {
  --cb-panel: var(--adm-card, #141b3f);
  --cb-paper: var(--adm-bg, #0f1536);
  --cb-hover: var(--adm-card-head, #1b2250);
  --cb-line: var(--adm-border, #26305e);
  --cb-ink: var(--adm-text-strong, #e6e9f7);
  --cb-body-text: var(--adm-text, #c3c9e2);
  --cb-muted: var(--adm-muted, #8a92b8);
  --cb-brand: var(--adm-accent, #4d72ff);
  --cb-brand-soft: var(--adm-accent-soft, #1a2250);
  --cb-brand-glow: var(--adm-accent-soft, rgba(77, 114, 255, 0.35));
  box-shadow: var(--adm-shadow, 0 -16px 48px rgba(0, 0, 0, 0.5));
}

/* Brand hairline along the top edge */
.cookie-banner::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, var(--cb-brand), var(--cb-signal));
}

.cookie-banner.minimized {
  height: 70px !important;
  max-height: 70px !important;
  overflow: hidden;
}

.minimized-banner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  height: 55px;
  background-color: var(--cb-panel);
}

.minimized-banner > span {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--cb-body-text);
  cursor: pointer;
  transition: color 0.15s ease;
}

.minimized-banner > span:hover {
  color: var(--cb-brand);
}

.banner-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.banner-header h3 {
  margin: 0;
  font-family: var(--cb-display-font);
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--cb-ink);
}

.btn-minimize {
  position: absolute;
  right: 20px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px 10px;
  font-size: 0.9rem;
  color: var(--cb-muted);
  border-radius: 6px;
  transition: color 0.15s ease, background-color 0.15s ease;
}

.btn-minimize:hover {
  color: var(--cb-ink);
  background-color: var(--cb-hover);
}

.cookie-content {
  max-width: 1000px;
  margin: 0 auto;
  padding: 22px 20px 20px;
}

.cookie-main {
  overflow: auto;
  max-height: 35vh;
  font-size: 0.95rem;
  line-height: 1.55;
}

.cookie-actions {
  margin-bottom: 50px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.cookie-details {
  max-height: 35vh;
  overflow-y: auto;
  transition: max-height 0.3s ease;
  scrollbar-width: thin;
  scrollbar-color: var(--cb-line) transparent;
}

.cookie-details::-webkit-scrollbar { width: 6px; }
.cookie-details::-webkit-scrollbar-track { background: transparent; }
.cookie-details::-webkit-scrollbar-thumb { background: var(--cb-line); border-radius: 3px; }
.cookie-details::-webkit-scrollbar-thumb:hover { background: var(--cb-muted); }

.consent-group {
  padding: 15px 0;
  border-bottom: 1px solid var(--cb-line);
}

.consent-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
}

.consent-header h4 {
  margin: 0 0 5px 0;
  font-family: var(--cb-display-font);
  font-size: 0.98rem;
  font-weight: 600;
  color: var(--cb-ink);
}

.consent-header p {
  margin: 0;
  font-size: 0.85rem;
  color: var(--cb-muted);
  line-height: 1.5;
}

.consent-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: none;
}

.consent-toggle input[type='checkbox'],
.consent-item input[type='checkbox'] {
  width: 16px;
  height: 16px;
  accent-color: var(--cb-brand);
  cursor: pointer;
}

.consent-toggle input[type='checkbox']:disabled {
  cursor: not-allowed;
  opacity: 0.75;
}

.required-label {
  font-family: var(--cb-mono-font);
  font-size: 0.66rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--cb-muted);
}

.consent-item {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 8px 0;
}

.consent-item label {
  font-size: 0.9rem;
  color: var(--cb-body-text);
  cursor: pointer;
}

.link {
  color: var(--cb-brand);
  font-weight: 600;
  text-decoration: none;
  transition: color 0.15s ease;
}

.link:hover {
  text-decoration: underline;
}

.consent-actions {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.consent-footer {
  margin-top: 15px;
  margin-bottom: 60px;
  font-size: 0.78rem;
  color: var(--cb-muted);
  text-align: center;
}

/* ---------- Buttons ---------- */
.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-family: var(--cb-display-font);
  font-weight: 600;
  font-size: 0.9rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  padding: 11px 22px;
  margin-right: 10px;
  background: var(--cb-brand);
  color: #fff;
  box-shadow: 0 6px 20px var(--cb-brand-glow);
  transition: transform 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease,
    color 0.18s ease, border-color 0.18s ease;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 28px var(--cb-brand-glow);
}

.btn-primary:focus-visible {
  outline: 2px solid var(--cb-brand);
  outline-offset: 2px;
}

.btn-primary:disabled {
  background-color: var(--cb-hover);
  color: var(--cb-muted);
  box-shadow: none;
  cursor: not-allowed;
}

/* Secondary variant (customize) — sits beside the solid accept button */
.btn-ghost {
  background: transparent;
  color: var(--cb-body-text);
  border: 1px solid var(--cb-line);
  box-shadow: none;
}

.btn-ghost:hover:not(:disabled) {
  background: var(--cb-brand-soft);
  border-color: var(--cb-brand);
  color: var(--cb-brand);
  box-shadow: none;
}

.btn-ghost:disabled {
  background: transparent;
  border-color: var(--cb-line);
  color: var(--cb-muted);
}

/* ---------- GDPR notice ---------- */
.gdpr-notice {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: var(--cb-brand-soft);
  border: 1px solid var(--cb-line);
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 6px;
}

.gdpr-notice i {
  color: var(--cb-brand);
  margin-top: 2px;
  flex: none;
}

.gdpr-notice p {
  margin: 0;
  font-size: 0.88rem;
  line-height: 1.5;
  color: var(--cb-body-text);
}

/* ---------- Data handling accordion ---------- */
.data-handling-section {
  margin: 20px 0;
  border: 1px solid var(--cb-line);
  border-radius: 10px;
  overflow: hidden;
}

.data-handling-header {
  padding: 14px 16px;
  background-color: var(--cb-paper);
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.data-handling-header:hover {
  background-color: var(--cb-hover);
}

.data-handling-header > i {
  color: var(--cb-muted);
  font-size: 0.8rem;
  transition: transform 0.2s ease;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 10px;
}

.header-content i {
  color: var(--cb-brand);
}

.header-content h4 {
  margin: 0;
  font-family: var(--cb-display-font);
  font-size: 1rem;
  font-weight: 600;
  color: var(--cb-ink);
}

.data-handling-more {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 4px 0 2px;
  font-weight: 600;
}
.data-handling-details {
  padding: 20px;
  background-color: var(--cb-panel);
  border-top: 1px solid var(--cb-line);
  transition: all 0.3s ease-in-out;
}

.info-group {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
}

.info-group:last-child {
  margin-bottom: 0;
}

.info-icon {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  background: var(--cb-brand-soft);
  color: var(--cb-brand);
  font-size: 0.9rem;
}

.info-content {
  flex-grow: 1;
}

.info-content h5 {
  margin: 0 0 10px 0;
  font-family: var(--cb-display-font);
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--cb-ink);
}

.info-content p {
  margin: 0;
  font-size: 0.88rem;
  line-height: 1.55;
  color: var(--cb-body-text);
}

.info-content ul {
  margin: 0;
  padding-left: 20px;
  font-size: 0.88rem;
  line-height: 1.5;
  color: var(--cb-body-text);
}

.info-content li {
  margin: 5px 0;
}

/* ---------- Language selector ---------- */
.language-selector {
  position: relative;
  margin-top: 10px;
  width: 150px;
}

.language-dropdown {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid var(--cb-line);
  border-radius: 8px;
  cursor: pointer;
  background: var(--cb-panel);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.language-dropdown:hover {
  border-color: var(--cb-brand);
  box-shadow: 0 4px 12px var(--cb-brand-glow);
}

.language-dropdown > i {
  color: var(--cb-muted);
  font-size: 0.7rem;
}

.selected-language {
  flex-grow: 1;
  font-size: 0.85rem;
  color: var(--cb-body-text);
}

.language-options {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--cb-panel);
  border: 1px solid var(--cb-line);
  border-radius: 10px;
  margin-top: 4px;
  padding: 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 16px 40px rgba(10, 15, 44, 0.16);
  scrollbar-width: thin;
  scrollbar-color: var(--cb-line) transparent;
}

.language-options::-webkit-scrollbar { width: 6px; }
.language-options::-webkit-scrollbar-track { background: transparent; }
.language-options::-webkit-scrollbar-thumb { background: var(--cb-line); border-radius: 3px; }
.language-options::-webkit-scrollbar-thumb:hover { background: var(--cb-muted); }

.language-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 7px;
  cursor: pointer;
  font-size: 0.85rem;
  color: var(--cb-body-text);
  transition: background-color 0.15s ease, color 0.15s ease;
}

.language-option:hover {
  background-color: var(--cb-hover);
  color: var(--cb-ink);
}
</style>
